import React, { PureComponent, useState, useEffect, createRef } from 'react';
import Pagination from 'components/v2/PaginationV2';
import { find, isEmpty, isNull, uniq, cloneDeep, isNil } from 'lodash';
import { LicenseManager } from 'ag-grid-enterprise';
import { titleize } from 'underscore.string';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import ColumnsSetStore from 'stores/ColumnsSetStore';
import ColumnsWebAPIUtils from 'utils/ColumnsWebAPIUtils';
import SessionStore from 'stores/SessionStore';
import FilterActionCreators from 'actions/FilterActionCreators';
import { getShipmentsByStream, getShipmentsCount, getUVCsvExport, createReport, updateReport, getReport, deleteReport, setFavoriteReport, } from 'utils/UnifiedViewWebAPIUtils';
import FiltersStore from 'stores/FiltersStore';
import OrderModule from 'components/modules/orders/Module';
import ShipmentModule from 'components/modules/shipments/ShipmentDetailsContainer';
import CargomaticModal from 'components/v2/modal';
import { useTranslation, withTranslation } from 'react-i18next';
import { getLocaleText } from 'utils/localization/agGridLocale';
import { withLocaleContext } from 'providers/locale-settings-context';
import USStates from 'constants/us-states';
import { isUS } from 'helpers/helper_functions';
import { sluggify, firstLetterTitlization } from 'utils/localization/utils';
import { Menu } from '@headlessui/react';
import Panel from 'components/v2/Panel';
import PanelSection from 'components/v2/PanelSection';
import DropdownItem from 'components/v2/dropdowns/DropdownItem';
import DropdownMenu from 'components/v2/dropdowns/DropdownMenu';
import Breadcrumbs from 'components/v2/Breadcrumbs';
import Favorite from './Favorite';
import Layout from './Layout';
import { shipmentHasEquipment, getStopGroupsArray } from './utils';
import BulkActions from './BulkActions';
import OrdersBulkActions from './orders/BulkActions';
import ShipmentExplorer from './ExploreShipmentUnifiedView';
import { getDrayageColumns, getShorthaulColumns } from './columns';
import SaveFilterModal from './SaveFilterModal';
import { IMPORT, DOMESTIC } from '../../../config/order-types';
import { STANDARD_REFERENCE_NUMBERS, SIDEBAR_STATUSES, TIME_SELECTIONS, } from './constants';
import { tagFormatter, statusFormatter, portIntegrationsFormatter, orderIDRenderer, shipmentIDRenderer, marginFormatter, deliveryOrderFormatter, checkedFormatter, lineSeparatedStringFormatter, } from './formatters';
import TextFilterWithExclude from './customFilters/TextFilterWithExclude';
import TextFilter from './customFilters/TextFilter';
import SaveModal from './SaveModal';
import DeleteReportModal from './DeleteReportModal';
import { getModelParams, loadColumnState, transformFilterModelQuery, getInitialDateRange, getMinValidDate, } from './transformers';
import FilterChips from './FilterChips';
import { withPageContext } from './PageContext';
import { withGroups } from '../../../hooks/unifiedview/useReports';
import { ColumnCategoriesDropdown } from './ColumnCategoriesDropdown';
import Alert from 'components/ui/Alert';
import { checkShorthaulAppointmentNeeded } from 'helpers/shipments';
import { coalesceItems, coalesceLegacyAndNewFields, } from 'utils/coalesceLegacyAndNewFields';
import coalesceColumnDefs from 'utils/coalesceColumnDefinitions';
import { showToast } from 'stores/ToastStore';
import OrderExplorer from './orders/Explore';
const getOptions = (t) => [
    {
        name: t('DRAYAGE'),
        value: 'drayage',
    },
    {
        name: t('SHORTHAUL'),
        value: 'shorthaul',
    },
];
LicenseManager.setLicenseKey(`${AG_GRID_LICENSE}`);
const ARRIVED_ORDER_TYPES = [IMPORT, DOMESTIC];
const POLLING_FREQUENCY = 60000; // 1 min
const INACTIVE_SHIPMENT_STATUS = ['Job Closed', 'Canceled'];
const DEFAULT_CREATED_AT_SORT = {
    colId: 'created_at',
    sort: 'desc',
    sortIndex: 99,
};
const createdAtDefaultFilterModel = {
    created_at: Object.assign(Object.assign({}, getInitialDateRange()), { filterType: 'date', type: 'inRange' }),
};
const perPageOptions = [100, 500];
const drayageFilterModel = {
    shipment_type: {
        condition1: {
            type: 'equals',
            filter: 'drayage',
            filterType: 'text',
        },
        condition2: {
            type: 'equals',
            filter: 'rail',
            filterType: 'text',
        },
        operator: 'OR',
    },
};
const shorthaulFilterModel = {
    shipment_type: {
        condition1: {
            type: 'equals',
            filter: 'shorthaul',
            filterType: 'text',
        },
        condition2: {
            type: 'equals',
            filter: 'longhaul',
            filterType: 'text',
        },
        condition3: {
            type: 'equals',
            filter: 'dayrate',
            filterType: 'text',
        },
        operator: 'OR',
    },
};
const sidebarParams = {
    toolPanels: [
        {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
                suppressRowGroups: false,
                suppressValues: false,
                suppressPivots: true,
                suppressPivotMode: true,
                suppressColumnFilter: false,
                suppressColumnSelectAll: false,
                suppressColumnExpandAll: false,
                suppressSyncLayoutWithGrid: true,
            },
        },
        {
            id: 'filters',
            labelDefault: 'Filters',
            labelKey: 'filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel',
            toolPanelParams: {
                suppressExpandAll: false,
                suppressFilterSearch: false,
            },
        },
    ],
};
const PerPage = (props) => {
    const { t } = useTranslation();
    return (React.createElement("div", { "data-testid": "div-db370b9d", className: "flex items-center my-auto mr-4 space-x-2" },
        React.createElement("label", { "data-testid": "label-122f0293", className: "form-label" }, t('PER_PAGE')),
        React.createElement("select", { "data-testid": "select-21575e42", onChange: props.onChange, value: props.selectedValue, className: "form-select" }, props.options.map((item, index) => {
            return (React.createElement("option", { "data-testid": `option-e3a17c30-${index}`, key: item, value: item }, item));
        }))));
};
PerPage.propTypes = {
    onChange: PropTypes.func.isRequired,
    selectedValue: PropTypes.number.isRequired,
    options: PropTypes.array.isRequired,
};
const FilterListComponent = (props) => {
    return (React.createElement("div", { "data-testid": "div-deb3fcfd" },
        React.createElement("fieldset", { "data-testid": "fieldset-27f5ee24" },
            React.createElement("span", { "data-testid": "span-1a7741ac", className: "form-label" }, props.name),
            props.filters.length === 0 && (React.createElement("div", { "data-testid": "div-11dd6ab9", className: "text-sm text-grey-dark" }, "No Filters Found...")),
            React.createElement("div", { "data-testid": "div-dcce3944", className: "flex" }, props.filters.map((filter, i) => {
                return (React.createElement("div", { "data-testid": `div-18634715-${i}`, className: "mr-2", key: i },
                    React.createElement("span", { "data-testid": `span-61cabd6a-${i}`, className: "mr-2 text-sm text-grey-darker" }, filter.name),
                    React.createElement("input", { "data-testid": `input-07f6fb46-${i}`, key: filter._id, type: "checkbox", value: "", checked: props.selectedFilter._id === filter._id, onChange: (e) => {
                            props.onFilterChange(filter, props.selectedFilter, props.filterSelected, e.target.value);
                        } })));
            })))));
};
FilterListComponent.propTypes = {
    name: PropTypes.string,
    filters: PropTypes.array,
    selectedFilter: PropTypes.object,
};
// A preliminary component for redirecting users from areas they do not have access too.
const CountDown = () => {
    const [count, setCount] = useState(3);
    useEffect(() => {
        const interval = setInterval(() => {
            if (count > 0) {
                setCount(count - 1);
            }
        }, 1000);
        // Cleanup the interval when the component unmounts
        return () => clearInterval(interval);
    }, [count]);
    if (count === 0) {
        window.location.href = '/';
    }
    return (React.createElement("div", { "data-testid": "div-9b2113eb", className: "py-32 flex w-full justify-center align-middle" },
        React.createElement("div", { "data-testid": "div-d4c2ae09", className: "flex flex-col justify-center align-middle" },
            React.createElement("div", { "data-testid": "div-e4259329", className: "flex justify-center py-2" },
                React.createElement("i", { "data-testid": "i-148a5ea3", className: "fa fa-spinner fa-spin text-5xl text-green" })),
            React.createElement("h4", { "data-testid": "h4-925412f4" },
                "Access not allowed. Redirecting in ",
                count,
                " seconds..."))));
};
class MasterList extends PureComponent {
    constructor(props) {
        super(props);
        // Stable function reference to avoid triggering grid updates each render
        this.getRowId = (params) => params.data._id;
        this.unifiedViewV2Enabled = () => { var _a; return ((_a = this.props) === null || _a === void 0 ? void 0 : _a.unifiedViewV2) === true; };
        this.UNSAFE_componentWillReceiveProps = (nextProps) => {
            if (nextProps.unifiedViewV2 !== this.props.unifiedViewV2) {
                this.setState({ unifiedViewV2: nextProps.unifiedViewV2 }, () => {
                    if (!isNil(this.state.unifiedViewV2)) {
                        // Abort any in-flight requests before reloading under new LD flag
                        this.abortControllers();
                        this.fetchData();
                    }
                });
            }
        };
        this.fetchData = () => {
            var _a, _b;
            this.stopPolling();
            if ((_b = (_a = this.props.match) === null || _a === void 0 ? void 0 : _a.params) === null || _b === void 0 ? void 0 : _b.reportId) {
                this.handleFetchReport();
            }
            else {
                this.loadBreadCrumbs();
                this.startPolling();
            }
        };
        this.handleUpdateRows = (updates) => {
            const map = updates.reduce((map, update) => {
                map[update._id] = update;
                return map;
            }, {});
            this.setState((prevState) => {
                const items = prevState.items.map((item) => {
                    if (map[item.shipment_oid]) {
                        return Object.assign(Object.assign({}, item), map[item.shipment_oid]);
                    }
                    return item;
                });
                return { items };
            });
        };
        this.handleToggleFavorite = async () => {
            if (!this.state.report) {
                return;
            }
            const isFavorite = !this.state.report.isFavorite;
            const { isSuccess } = await setFavoriteReport({
                id: this.state.report._id,
                isFavorite,
            });
            if (!isSuccess) {
                return;
            }
            this.setState((prevState) => ({
                report: Object.assign(Object.assign({}, prevState.report), { isFavorite }),
            }));
        };
        this.handleColumnUpdate = (update) => {
            const { columns, event } = update;
            if (event === 'CREATE') {
                this.setState({
                    columns,
                }, () => {
                    // this.reset();
                });
            }
            else {
                this.setState({
                    columns,
                });
            }
        };
        this.onHideShipmentModal = () => {
            this.setState({
                showShipmentModal: false,
            });
        };
        this.onHideModal = () => {
            this.setState({
                showOrderModal: false,
            });
        };
        this.handleFetchReport = async () => {
            const { match: { params }, t, } = this.props;
            try {
                const report = await getReport({
                    queryKey: ['getReport', params.reportId],
                });
                await this.handleFilterSelect(report);
                this.setState({ report }, () => {
                    this.loadBreadCrumbs();
                    this.startPolling();
                });
            }
            catch (_e) {
                showToast(t('ERROR_FETCHING_REPORT'), null, 'error');
            }
        };
        this.onEditingStopped = (event) => {
            if (!event.valueChanged || !event.colDef.updateFunc) {
                return;
            }
            event.colDef.updateFunc(event);
        };
        this.onFilterChange = () => {
            if (this.state.isQuickFiltered)
                return;
            this.setState({
                filterChanged: true,
            }, () => {
                this.handlePagination(1);
            });
        };
        this.onSortChanged = () => {
            if (this.state.isQuickFiltered)
                return;
            this.getFilterResults();
        };
        this.handleFiltersUpdate = (update) => {
            var _a;
            const { filters, event } = update;
            const localFilters = filters
                .filter((filter) => filter.shared !== true)
                .sort((a, b) => { var _a; return (((_a = a === null || a === void 0 ? void 0 : a.name) === null || _a === void 0 ? void 0 : _a.trim()) || '').localeCompare(b.name.trim()); });
            const sharedFilters = filters
                .filter((filter) => filter.shared === true)
                .sort((a, b) => { var _a, _b; return (((_a = a === null || a === void 0 ? void 0 : a.name) === null || _a === void 0 ? void 0 : _a.trim()) || '').localeCompare((_b = b === null || b === void 0 ? void 0 : b.name) === null || _b === void 0 ? void 0 : _b.trim()); });
            if (event === 'CREATED') {
                const { result: { shared, _id }, } = update;
                const cols = [];
                (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.getAllGridColumns().forEach((col) => {
                    const { userProvidedColDef, visible } = col;
                    if (!isNull(userProvidedColDef)) {
                        const { field } = userProvidedColDef;
                        if (visible) {
                            cols.push(field);
                        }
                    }
                });
                const filter_id = _id;
                if (!shared) {
                    ColumnsWebAPIUtils.createCols({ filter_id, cols });
                }
            }
            this.setState({
                localFilters,
                sharedFilters,
            });
        };
        this.handleRowDisplayed = () => {
            var _a;
            const rowCount = (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.getDisplayedRowCount();
            if (rowCount !== this.state.rowCount) {
                this.setState({ rowCount });
            }
        };
        this.handleFiltersStoreUpdate = (row) => {
            var _a, _b, _c;
            const { add, update } = row;
            if (this.state.isFirst) {
                (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.batchUpdateRowData({ add });
                this.setState({
                    isFirst: false,
                });
            }
            else {
                if (add.length > 0) {
                    (_b = this.gridRef.current) === null || _b === void 0 ? void 0 : _b.api.batchUpdateRowData({ add });
                    this.handleRowDisplayed();
                }
                if (update.length > 0) {
                    (_c = this.gridRef.current) === null || _c === void 0 ? void 0 : _c.api.batchUpdateRowData({ update });
                }
            }
        };
        this.getSavedFilterModel = (filter) => {
            var _a, _b;
            // move primaryDate to model to have backward compatibility
            if (filter.primaryDate && ((_a = filter.timeSelected) === null || _a === void 0 ? void 0 : _a.length)) {
                // check if model has same column filter we do nothing
                if (!(filter.model && filter.model[filter.primaryDate])) {
                    // model can not exist
                    filter.model = filter.model || {};
                    // set primary
                    const timeSelectedType = (_b = TIME_SELECTIONS.find((ts) => ts.name === filter.timeSelected)) === null || _b === void 0 ? void 0 : _b.localizationSlug;
                    if (timeSelectedType) {
                        filter.model[filter.primaryDate] = {
                            dateFrom: null,
                            dateTo: null,
                            filterType: 'date',
                            type: timeSelectedType,
                        };
                    }
                }
            }
            if (!filter.model) {
                return {};
            }
            const mappedFilterModel = Object.keys(filter.model).reduce((model, key) => {
                const value = filter.model[key];
                model[key] = value;
                // remove empty filters
                if (Array.isArray(value.value) && value.value.length === 0) {
                    delete model[key];
                }
                // remove not used condition to not treat them as new default condition, see: CARGO-7936
                const notUsedConditions = ['contains', 'notContains'];
                // plain filter
                if (notUsedConditions.includes(value.type)) {
                    delete model[key];
                }
                // filter with multiple conditions
                const conditions = Object.keys(value).filter((key) => key.startsWith('condition'));
                if (conditions.length) {
                    conditions.forEach((conditionKey) => {
                        if (notUsedConditions.includes(value[conditionKey].type)) {
                            delete model[key];
                        }
                        // if array of conditions
                        if (Array.isArray(value[conditionKey]) &&
                            value[conditionKey].length) {
                            value[conditionKey].forEach((conditionFilter) => {
                                if (notUsedConditions.includes(conditionFilter.type)) {
                                    delete model[key];
                                }
                            });
                        }
                    });
                }
                return model;
            }, {});
            return this.unifiedViewV2Enabled()
                ? coalesceLegacyAndNewFields(mappedFilterModel)
                : mappedFilterModel;
        };
        this.setCustomFilterModels = (savedFilterModel) => {
            const customFilterColumns = [
                'shipment_id',
                'order_id',
                'orderStatus',
                'chassis_number',
                'shipper_name',
                'reference_numbers',
                ...STANDARD_REFERENCE_NUMBERS,
            ];
            const stopColumnFields = this.state.columnDefs
                .filter((c) => c.children)
                .flatMap((c) => c.children)
                .map((c) => c.field);
            const customFilterStopColumns = stopColumnFields.filter((f) => f &&
                (f.endsWith('stop_equipment_id') || f.endsWith('stop_chassis_number')));
            const customFilterFields = [
                ...customFilterColumns,
                ...customFilterStopColumns,
            ];
            customFilterFields.forEach((field) => {
                var _a;
                const filterInstance = (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.getFilterInstance(field);
                if (!isEmpty(filterInstance)) {
                    filterInstance.setModel(savedFilterModel && savedFilterModel[field]
                        ? savedFilterModel[field]
                        : null);
                }
            });
        };
        this.handleFilterSelect = async (filter) => {
            if (filter._id !== this.state.selectedFilter._id) {
                let mappedColumns;
                this.setState((prevState) => {
                    const { columns } = prevState;
                    const columnSet = find(columns, (col) => {
                        return col.filter_id === filter._id;
                    });
                    mappedColumns = this.getColumnDefs(filter.view, this.props.t);
                    const mappedFilterModel = this.getSavedFilterModel(filter);
                    return {
                        view: filter.view,
                        selectedFilter: filter,
                        filterSelected: true,
                        activeCols: columnSet ? columnSet._id : null,
                        applyingSavedFilters: true,
                        columnDefs: mappedColumns,
                        filterModel: Object.assign({}, mappedFilterModel),
                    };
                }, async () => {
                    var _a, _b, _c, _d, _f, _g, _h;
                    (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.autoSizeColumns();
                    await ((_b = this.gridRef.current) === null || _b === void 0 ? void 0 : _b.api.setGridOption('columnDefs', mappedColumns));
                    loadColumnState(filter, (_c = this.gridRef.current) === null || _c === void 0 ? void 0 : _c.api, [], this.unifiedViewV2Enabled());
                    this.setCustomFilterModels(this.state.filterModel);
                    (_d = this.gridRef.current) === null || _d === void 0 ? void 0 : _d.api.setFilterModel(this.state.filterModel);
                    (_f = this.gridRef.current) === null || _f === void 0 ? void 0 : _f.api.onFilterChanged();
                    if (filter.rowGroupColumns && ((_g = filter.rowGroupColumns) === null || _g === void 0 ? void 0 : _g.length) > 0) {
                        (_h = this.gridRef.current) === null || _h === void 0 ? void 0 : _h.api.setRowGroupColumns(filter.rowGroupColumns);
                    }
                });
            }
            else {
                this.setState({
                    filterSelected: false,
                    selectedFilter: { name: '', _id: '' },
                    applyingSavedFilters: true,
                    filterModel: null,
                }, () => {
                    var _a, _b;
                    this.setCustomFilterModels(null);
                    (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.setFilterModel(null);
                    (_b = this.gridRef.current) === null || _b === void 0 ? void 0 : _b.api.onFilterChanged();
                });
            }
        };
        this.exportCsv = async () => {
            var _a, _b;
            // Use the same params from the current query to ensure export matches
            // what the user sees. This ensures all filters (including set filters)
            // are properly included and have already been transformed.
            const params = this.state.queryPayload.params;
            const options = {
                sort: this.getSortModel(),
                viewType: this.state.view === 'drayage' ? 'intermodal' : 'shorthaul',
            };
            const sameDay = moment(this.state.dateTo).isSame(moment(this.state.dateFrom, 'day'));
            let columns = (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.getAllGridColumns().filter((c) => c.visible && c.colDef.field).map((c) => {
                // BE expecting field for data mapping and actual headerName for csv header
                return {
                    field: c.colDef.field,
                    headerName: c.colDef.headerName,
                };
            });
            // Apply coalescing to columns if unifiedViewV2 is enabled
            if (this.unifiedViewV2Enabled()) {
                columns = coalesceColumnDefs(columns);
            }
            (_b = this.gridRef.current) === null || _b === void 0 ? void 0 : _b.api.showLoadingOverlay();
            this.setState({ exportLoading: true }, async () => {
                var _a;
                if (this.state.primaryDate) {
                    params[this.state.primaryDate] = {
                        dateFrom: this.state.dateFrom,
                        dateTo: this.state.dateTo,
                        type: sameDay ? 'equals' : 'inRange',
                        filterType: 'date',
                    };
                }
                // Removed unifiedViewV2Enabled to let function default handle proxy through mono
                await getUVCsvExport({
                    params,
                    options,
                    columns,
                });
                (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.hideOverlay();
                this.setState({ exportLoading: false });
            });
        };
        this.hideSaveFilterModal = () => {
            this.setState({ showSaveFilterModal: false });
        };
        /**
         * Common function to reset the view with default filters and settings
         * @param {string} view - The view type ('drayage' or 'shorthaul')
         * @param {boolean} resetView - Whether to reset the view type to default
         */
        this.resetView = (view = 'drayage', resetView = false) => {
            var _a, _b;
            // Clear all current filters
            (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.setFilterModel(null);
            (_b = this.gridRef.current) === null || _b === void 0 ? void 0 : _b.api.setQuickFilter(null);
            // Determine the target view
            const targetView = resetView ? 'drayage' : view;
            // Get appropriate column definitions and filter model for the view
            const columnDefs = this.getColumnDefs(targetView, this.props.t);
            const model = targetView === 'drayage' ? drayageFilterModel : shorthaulFilterModel;
            // Create default query payload with proper date filters and view settings
            const queryPayload = {
                options: {
                    page: 1,
                    per_page: 100,
                    sort: [DEFAULT_CREATED_AT_SORT],
                    viewType: targetView === 'drayage' ? 'intermodal' : 'shorthaul',
                },
                params: Object.assign(Object.assign({}, model), createdAtDefaultFilterModel),
            };
            // Reset state with default values
            this.setState({
                filterSelected: false,
                selectedFilter: { id: '', name: '' },
                activeCols: null,
                columnDefs,
                queryPayload,
                activePage: 1,
                filterModel: {},
                view: targetView,
                // Reset selection states
                selectedShipments: [],
                selectedShipmentsForAccessorials: [],
                selectedShipmentsForYards: [],
                changeCarrierDisabled: true,
                changeShipperDisabled: true,
                arrivedActionDisabled: true,
                bulkUploadDocumentsDisabled: true,
                isSameShipperOrMasterShipper: false,
            }, () => {
                var _a, _b, _c, _d, _f, _g;
                // Apply the reset to the grid
                (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.setColumnDefs(this.state.columnDefs);
                (_b = this.gridRef.current) === null || _b === void 0 ? void 0 : _b.api.autoSizeColumns();
                (_c = this.gridRef.current) === null || _c === void 0 ? void 0 : _c.api.resetColumnState();
                this.setCustomFilterModels(null);
                (_d = this.gridRef.current) === null || _d === void 0 ? void 0 : _d.api.setFilterModel(null);
                (_f = this.gridRef.current) === null || _f === void 0 ? void 0 : _f.api.onFilterChanged();
                // Clear any selected rows
                (_g = this.gridRef.current) === null || _g === void 0 ? void 0 : _g.api.deselectAll();
                // Trigger data refresh with new filters
                this.getFilterResults();
            });
        };
        this.reset = () => {
            // Use the common resetView function with current view
            this.resetView(this.state.view, false);
        };
        /**
         * Reset to default view (drayage) with all default settings
         */
        this.resetToDefault = () => {
            // Use the common resetView function to reset to default drayage view
            this.resetView('drayage', true);
        };
        this.handleViewChange = (view) => {
            // Use the common resetView function when changing views
            this.resetView(view, false);
        };
        this.handleEntityTypeChange = (entityType) => {
            this.setState({ selectedEntityType: entityType });
            const newPath = entityType === 'order'
                ? '/unifiedview/explore/orders'
                : '/unifiedview/explore';
            window.history.replaceState(null, '', newPath);
        };
        this.getRowGroupColumns = () => {
            var _a;
            return (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.getRowGroupColumns().map((col) => col.colId);
        };
        this.saveAs = (name) => {
            var _a;
            const { shared, view } = this.state;
            const model = cloneDeep((_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.getFilterModel());
            const rowGroupColumns = this.getRowGroupColumns();
            const rowColumnsSize = this.getColumnsSize();
            const rowColumnsSorting = this.getColumnSorting();
            const canEdit = false;
            const { columnOrder, pinned, hidden } = this.getColumnState();
            const newFilter = {
                name,
                model,
                rowGroupColumns,
                shared,
                view,
                canEdit,
                pinned,
                hidden,
                columnOrder,
                rowColumnsSize,
                rowColumnsSorting,
            };
            FilterActionCreators.createFilter(newFilter);
            this.hideSaveFilterModal();
        };
        this.update = (name) => {
            var _a, _b;
            const { selectedFilter, shared, view } = this.state;
            let _id;
            const model = cloneDeep((_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.getFilterModel());
            const rowGroupColumns = this.getRowGroupColumns();
            const rowColumnsSize = this.getColumnsSize();
            const rowColumnsSorting = this.getColumnSorting();
            const { columnOrder, pinned, hidden } = this.getColumnState();
            const updates = {
                id: selectedFilter._id,
                model,
                rowGroupColumns,
                name,
                shared,
                view,
                pinned,
                hidden,
                columnOrder,
                rowColumnsSize,
                rowColumnsSorting,
            };
            FilterActionCreators.updateFilter(updates);
            if (this.state.activeCols) {
                _id = this.state.activeCols;
                const cols = [];
                (_b = this.gridRef.current) === null || _b === void 0 ? void 0 : _b.api.getAllGridColumns().forEach((col) => {
                    const { userProvidedColDef, visible } = col;
                    if (!isNull(userProvidedColDef)) {
                        const { field } = userProvidedColDef;
                        if (visible) {
                            cols.push(field);
                        }
                    }
                });
                if (!shared) {
                    ColumnsWebAPIUtils.updateCols({ _id, cols });
                }
            }
            this.hideSaveFilterModal();
        };
        this.toggleShare = () => {
            const { shared } = this.state;
            this.setState({
                shared: !shared,
            });
        };
        this.handleRowSelection = () => {
            var _a;
            const selectedRows = ((_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.getSelectedRows()) || [];
            let arrivedActionDisabled = true;
            let changeCustomerDisabled = false;
            if (selectedRows.length) {
                arrivedActionDisabled = !selectedRows.every((s) => !!s.order_oid && ARRIVED_ORDER_TYPES.includes(s.order_type));
                changeCustomerDisabled = selectedRows.some((s) => INACTIVE_SHIPMENT_STATUS.includes(s.shipmentStatus));
                const sameShipperOrMasterShipper = this.areShipmentsWithSameShipperOrMasterShipper(selectedRows);
                const changeCarrierDisabled = !this.areShipmentsWithSameShipper(selectedRows) ||
                    this.areShorthaulAppointmentNeeded(selectedRows);
                const bulkUploadDocumentsDisabled = !sameShipperOrMasterShipper;
                const selectedIds = this.state.selectedEntityType === 'order'
                    ? uniq(selectedRows.map((s) => s.order_oid))
                    : uniq(selectedRows.map((s) => s.shipment_oid));
                const selectedShipmentsForAccessorials = uniq(selectedRows
                    .filter((s) => s.shipment_type === 'drayage' && shipmentHasEquipment(s))
                    .map((shipment) => {
                    const { shipment_oid: _id, shipment_id, order_oid: orderId, } = shipment;
                    return Object.assign(Object.assign({}, shipment), { _id,
                        shipment_id, stops: getStopGroupsArray(shipment), orderId });
                }));
                const selectedShipmentsForYards = uniq(selectedRows
                    .filter((s) => s.shipment_type === 'drayage' && shipmentHasEquipment(s))
                    .map((shipment) => {
                    // get equipment ids from stop groups
                    const stopGroups = getStopGroupsArray(shipment);
                    const mappedStopEquipments = stopGroups.flatMap((s) => {
                        const equipments = s.stop_equipment_oid !== '' ? [s.stop_equipment_id] : [];
                        return equipments;
                    });
                    // distinct
                    const equipments = (mappedStopEquipments === null || mappedStopEquipments === void 0 ? void 0 : mappedStopEquipments.length) > 0
                        ? [...new Set(mappedStopEquipments)]
                        : [];
                    return {
                        id: shipment.shipment_oid,
                        equipments: equipments,
                        shipment_id: shipment.shipment_id,
                        market_id: shipment.market_code,
                    };
                }));
                this.setState({
                    arrivedActionDisabled,
                    changeCarrierDisabled: changeCustomerDisabled || changeCarrierDisabled,
                    changeShipperDisabled: changeCustomerDisabled,
                    bulkUploadDocumentsDisabled,
                    isSameShipperOrMasterShipper: sameShipperOrMasterShipper,
                    selectedShipments: selectedIds,
                    selectedShipmentsForAccessorials,
                    selectedShipmentsForYards,
                });
            }
            else {
                this.setState({
                    arrivedActionDisabled,
                    changeCarrierDisabled: changeCustomerDisabled,
                    changeShipperDisabled: changeCustomerDisabled,
                    selectedShipments: [],
                    selectedShipmentsForAccessorials: [],
                    selectedShipmentsForYards: [],
                });
            }
        };
        this.getDataTotal = () => {
            const { userAllowed } = this.state;
            if (!userAllowed) {
                return;
            }
            this.setState(async (prevState) => {
                this.abortCountController = new AbortController();
                // Removed unifiedViewV2Enabled to let function default handle proxy through mono
                const totalCount = await getShipmentsCount({
                    params: Object.assign({}, prevState.queryPayload.params),
                    options: {
                        viewType: prevState.view === 'drayage' ? 'intermodal' : 'shorthaul',
                    },
                }, { controller: this.abortCountController });
                const pages = Math.ceil(totalCount / prevState.queryPayload.options.per_page);
                this.setState({ totalCount, pages });
            });
        };
        this.getSortModel = () => {
            var _a;
            return (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.getColumnState().filter((col) => !!col.sort);
        };
        this.getSortModelInsteadOfDefaultSort = () => {
            var _a;
            return (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.getColumnState().filter((col) => !!col.sort && col.sortIndex < DEFAULT_CREATED_AT_SORT.sortIndex);
        };
        this.isCreatedAtSortApplied = (columnsState) => {
            return columnsState.find((item) => item.colId === DEFAULT_CREATED_AT_SORT.colId &&
                item.sortIndex !== DEFAULT_CREATED_AT_SORT.sortIndex &&
                item.sortIndex !== null);
        };
        this.getFilterResults = (isPolling = false, useStateFilterModel = false) => {
            const { userAllowed, activePage: page, queryPayload: { options: { per_page }, }, } = this.state;
            this.setState({ isFetchingData: true }, async () => {
                var _a, _b, _c;
                if (!userAllowed) {
                    return;
                }
                if ((_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api) {
                    if (!isPolling) {
                        (_b = this.gridRef.current) === null || _b === void 0 ? void 0 : _b.api.showLoadingOverlay();
                    }
                    // make the query string for the shared/pesonal filters
                    let params = this.transformFilterModelQuery(useStateFilterModel);
                    const options = {
                        sort: this.getSortModelInsteadOfDefaultSort(),
                        per_page,
                        page,
                        viewType: this.state.view === 'drayage' ? 'intermodal' : 'shorthaul',
                    };
                    const columnsState = (_c = this.gridRef.current) === null || _c === void 0 ? void 0 : _c.api.getColumnState();
                    if (!this.isCreatedAtSortApplied(columnsState)) {
                        const createdAtSort = columnsState.find((col) => col.colId === DEFAULT_CREATED_AT_SORT.colId);
                        if (createdAtSort) {
                            createdAtSort.sort = DEFAULT_CREATED_AT_SORT.sort;
                            createdAtSort.sortIndex = DEFAULT_CREATED_AT_SORT.sortIndex;
                            options.sort.push(Object.assign({}, createdAtSort));
                        }
                    }
                    this.setState((previousState) => {
                        return Object.assign(Object.assign({}, previousState), { loadingData: true, activePage: page || 1, queryPayload: Object.assign(Object.assign({}, previousState.queryPayload), { params,
                                options }) });
                    }, async () => {
                        // Any pending requests should abort.
                        this.abortControllers();
                        this.abortQueryController = new AbortController();
                        // Get Count
                        this.getDataTotal();
                        // Get Query Results
                        // Removed unifiedViewV2Enabled to let function default handle proxy through mono
                        const data = await getShipmentsByStream({
                            params,
                            options,
                        }, {
                            controller: this.abortQueryController,
                        });
                        this.handleDataStream(data);
                        this.setState({ isFetchingData: false });
                    });
                }
            });
        };
        this.handleDataStream = async (data) => {
            try {
                const { items: incomingItems } = data;
                const items = this.unifiedViewV2Enabled()
                    ? coalesceItems(incomingItems)
                    : incomingItems;
                this.setState(() => {
                    const updates = {
                        items,
                        loadingData: false,
                        applyingSavedFilters: false,
                    };
                    return updates;
                });
            }
            catch (e) {
                console.error(e);
            }
        };
        this.handlePerPageChange = (per_page) => {
            this.setState((prevState) => ({
                activePage: 1,
                queryPayload: Object.assign(Object.assign({}, prevState.queryPayload), { options: Object.assign(Object.assign({}, prevState.queryPayload.options), { per_page: parseInt(per_page, 10) }) }),
            }), () => this.getFilterResults());
        };
        this.handlePagination = (page) => {
            const { activePage, isFetchingData } = this.state;
            if (page === activePage && isFetchingData)
                return;
            this.setState({ activePage: page }, () => {
                this.getFilterResults();
            });
        };
        this.areShipmentsWithSameShipper = (selectedRows) => {
            var _a;
            if (selectedRows.length === 1) {
                return true;
            }
            return (((_a = selectedRows[0]) === null || _a === void 0 ? void 0 : _a.shipper_oid) &&
                selectedRows.every((shipment) => {
                    return shipment.shipper_oid === selectedRows[0].shipper_oid;
                }));
        };
        this.areShipmentsWithSameShipperOrMasterShipper = (selectedRows) => {
            var _a, _b;
            if (selectedRows.length === 1) {
                return true;
            }
            const sameShipper = ((_a = selectedRows[0]) === null || _a === void 0 ? void 0 : _a.shipper_oid) &&
                selectedRows.every((shipment) => {
                    var _a;
                    return (shipment === null || shipment === void 0 ? void 0 : shipment.shipper_oid) === ((_a = selectedRows[0]) === null || _a === void 0 ? void 0 : _a.shipper_oid);
                });
            const sameMasterShipper = ((_b = selectedRows[0]) === null || _b === void 0 ? void 0 : _b.master_account_id) &&
                selectedRows.every((shipment) => {
                    var _a;
                    return ((shipment === null || shipment === void 0 ? void 0 : shipment.master_account_id) === ((_a = selectedRows[0]) === null || _a === void 0 ? void 0 : _a.master_account_id));
                });
            return sameShipper || sameMasterShipper;
        };
        this.handleShowSaveReportModal = () => {
            this.setState({ showSaveModal: true, isSavingNewReport: false });
        };
        this.handleHideSaveReportModal = () => {
            this.setState({ showSaveModal: false });
        };
        this.handleShowSaveNewReportModal = () => {
            this.setState({ showSaveModal: true, isSavingNewReport: true });
        };
        this.handleShowConfirmDeleteReportModal = () => {
            this.setState({ showConfirmDeleteReportModal: true });
        };
        this.handleHideConfirmDeleteReportModal = () => {
            this.setState({ showConfirmDeleteReportModal: false });
        };
        this.handleConfirmDelete = async () => {
            const { report } = this.state;
            if (!report) {
                return;
            }
            await deleteReport(Object.assign(Object.assign({}, report), { id: report._id }));
            this.handleHideConfirmDeleteReportModal();
            this.props.history.push('/unifiedview/explore', { shallow: true });
            this.setState({ report: null }, () => {
                this.loadBreadCrumbs();
            });
        };
        this.handleSaveReport = async (report) => {
            var _a;
            const id = report === null || report === void 0 ? void 0 : report.id;
            this.handleHideSaveReportModal();
            const { view } = this.state;
            const model = cloneDeep((_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.getFilterModel());
            const rowGroupColumns = this.getRowGroupColumns();
            const rowColumnsSize = this.getColumnsSize();
            const rowColumnsSorting = this.getColumnSorting();
            const canEdit = false;
            const { columnOrder, pinned, hidden } = this.getColumnState();
            let reportToSave = report;
            const filterType = this.props.location.pathname.includes('order')
                ? 'order'
                : 'shipment';
            if (id) {
                const updatedReport = Object.assign({ model,
                    rowGroupColumns,
                    view,
                    pinned,
                    hidden,
                    columnOrder,
                    rowColumnsSize,
                    rowColumnsSorting }, report);
                reportToSave = await updateReport(updatedReport);
            }
            else {
                const newReport = Object.assign({ model,
                    rowGroupColumns,
                    view,
                    canEdit,
                    pinned,
                    hidden,
                    columnOrder,
                    rowColumnsSize,
                    rowColumnsSorting,
                    filterType }, report);
                reportToSave = await createReport(newReport);
                this.props.history.push(`/unifiedview/reports/${reportToSave._id}`, {
                    shallow: true,
                });
            }
            this.setState({ report: reportToSave }, () => {
                this.loadBreadCrumbs();
            });
        };
        this.removeFilter = (filterName) => {
            var _a;
            const filterModel = cloneDeep((_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.getFilterModel());
            delete filterModel[filterName];
            this.setState({ filterModel }, () => {
                var _a, _b;
                (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.setFilterModel(filterModel);
                (_b = this.gridRef.current) === null || _b === void 0 ? void 0 : _b.api.onFilterChanged();
            });
        };
        this.gridRef = createRef();
        this.localeText = getLocaleText();
        this.state = {
            gridReady: false,
            unifiedViewV2: props.unifiedViewV2,
            breadcrumbs: { pages: [] },
            report: null,
            group: null,
            isSavingNewReport: false,
            showConfirmDeleteReportModal: false,
            showSaveModal: false,
            showSaveNewModal: false,
            sidebarState: 'expanded',
            columnsChanged: false,
            filterChanged: false,
            applyingSavedFilters: false,
            showDateInfoAlert: true,
            filterModel: {},
            stopGroupStats: {},
            userAllowed: SessionStore.canAccessUnifiedView(), // For now this is limited to admin users
            changeCarrierDisabled: true,
            changeShipperDisabled: true,
            arrivedActionDisabled: true,
            columnDefs: this.getColumnDefs('drayage', props.t),
            view: 'drayage',
            selectedEntityType: window.location.pathname.includes('/explore/orders')
                ? 'order'
                : 'shipment',
            columns: [],
            statusBar: { statusPanels: [{ statusPanel: 'agAggregationComponent' }] },
            frameworkComponents: {
                deliveryOrderFormatter,
                statusFormatter: statusFormatter,
                portIntegrationsFormatter: portIntegrationsFormatter,
                checkedFormatter: checkedFormatter,
                lineSeparatedStringFormatter: lineSeparatedStringFormatter,
                tagFormatter: tagFormatter,
                orderIDRenderer: orderIDRenderer,
                shipmentIDRenderer: shipmentIDRenderer,
                marginFormatter: marginFormatter,
                textFilterWithExclude: TextFilterWithExclude,
                textFilter: TextFilter,
            },
            showSaveFilterModal: false,
            localFilters: [],
            sharedFilters: [],
            filterSelected: false,
            canShare: SessionStore.canShareFilters(),
            shared: false,
            groupDefaultExpanded: 2,
            selectedFilter: {
                name: '',
                id: '',
            },
            order_oid: '',
            showOrderModal: false,
            shipment_oid: '',
            showShipmentModal: false,
            isFirst: true,
            bulkUploadDocumentsDisabled: true,
            isSameShipperOrMasterShipper: false,
            getRowNodeId: (data) => data._id,
            selectedShipments: [],
            selectedShipmentsForAccessorials: [],
            selectedShipmentsForYards: [],
            loadingData: true,
            activePage: 1,
            pages: 1,
            queryPayload: {
                params: {},
                options: {
                    viewType: 'intermodal',
                    per_page: 100,
                    sort: [DEFAULT_CREATED_AT_SORT],
                },
            },
            shouldShowDefaultFilterInfo: true,
            rowCount: 0,
            isFetchingData: false,
        };
    }
    getColumnDefs(view, t) {
        const columns = view === 'drayage'
            ? getDrayageColumns(this.unifiedViewV2Enabled())
            : getShorthaulColumns();
        const processed = columns
            .map((incoming) => {
            const column = Object.assign({}, incoming);
            column.menuTabs = ['filterMenuTab', 'generalMenuTab'];
            if (column.field === 'created_at') {
                column.filterParams.minValidDate = getMinValidDate();
            }
            if (column.field === 'order_id') {
                column.cellRendererParams = {
                    innerRenderer: (props) => {
                        return props.value;
                    },
                    onOrderClick: (data) => {
                        const { order_oid } = data;
                        this.setState({
                            order_oid,
                            showOrderModal: true,
                        });
                    },
                };
            }
            else if (column.field === 'shipment_id') {
                column.cellRendererParams = {
                    innerRenderer: (props) => {
                        return props.value;
                    },
                    onShipmentClick: (data) => {
                        const { shipment_oid } = data;
                        this.setState({
                            shipment_oid,
                            showShipmentModal: true,
                        });
                    },
                };
            }
            if (isUS() &&
                column.field &&
                column.field.toLowerCase().includes('state')) {
                column.filter = 'agSetColumnFilter';
                column.floatingFilter = true;
                column.filterParams = {
                    values: USStates.map((state) => state.abbreviation),
                };
            }
            return column;
        })
            .map((c) => {
            if (c.field === 'margin') {
                c.cellRenderer = 'marginFormatter';
            }
            c.headerName = c.titleize
                ? firstLetterTitlization(t(sluggify(c.headerName)))
                : t(sluggify(c.headerName));
            c.headerTooltip = t(sluggify(c.headerTooltip));
            return c;
        });
        return this.unifiedViewV2Enabled()
            ? coalesceColumnDefs(processed)
            : processed;
    }
    async startPolling() {
        this.getFilterResults(true, true);
        this.pollingTimer = setInterval(() => this.getFilterResults(true), POLLING_FREQUENCY);
    }
    stopPolling() {
        clearInterval(this.pollingTimer);
    }
    async componentDidMount() {
        FiltersStore.addChangeListener(this.handleFiltersStoreUpdate);
        FiltersStore.addFiltersChangeListener(this.handleFiltersUpdate);
        ColumnsSetStore.addChangeListener(this.handleColumnUpdate);
        FilterActionCreators.getFilterNames();
        ColumnsWebAPIUtils.getCols();
        document.title = 'Cargomatic Inc., - Command: Unified View';
        this.setState({ unifiedViewV2: this.props.unifiedViewV2 });
    }
    abortControllers() {
        // Count and queries abort controllers.
        if (this.abortCountController) {
            if (!this.abortCountController.signal.aborted) {
                this.abortCountController.abort();
            }
        }
        if (this.abortQueryController) {
            if (!this.abortQueryController.signal.aborted) {
                this.abortQueryController.abort();
            }
        }
    }
    componentWillUnmount() {
        FiltersStore.removeChangeListener(this.handleFiltersStoreUpdate);
        FiltersStore.removeFiltersChangeListener(this.handleFiltersUpdate);
        ColumnsSetStore.removeChangeListener(this.handleColumnUpdate);
        this.stopPolling();
        document.title = 'Cargomatic Inc. - Command';
    }
    getModelParams(useStateFilterModel = false) {
        var _a, _b;
        const { view } = this.state;
        const filterModel = cloneDeep(useStateFilterModel
            ? this.state.filterModel
            : (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.getFilterModel());
        return getModelParams({
            view,
            filterModel,
            agGridApi: (_b = this.gridRef.current) === null || _b === void 0 ? void 0 : _b.api,
        });
    }
    getColumnState() {
        var _a;
        const columnState = (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.getColumnState();
        const pinned = columnState
            .filter((c) => c.pinned)
            .map((c) => ({ field: c.colId, pinned: c.pinned }));
        const hidden = columnState.filter((c) => c.hide).map((c) => c.colId);
        const columnOrder = columnState.map((c) => c.colId);
        return { columnOrder, pinned, hidden };
    }
    getColumnsSize() {
        var _a;
        const columnsState = (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.getColumnState();
        return columnsState.map((columnState) => {
            return { field: columnState.colId, size: columnState.width };
        });
    }
    getColumnSorting() {
        var _a;
        const columnsState = (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api.getColumnState();
        return columnsState.map((columnState) => {
            return {
                field: columnState.colId,
                sort: columnState.sort,
                sortIndex: columnState.sortIndex,
            };
        });
    }
    areShorthaulAppointmentNeeded(selectedRows) {
        return selectedRows.some((shipment) => {
            var _a;
            const { shipment_type, shipmentStatus } = shipment;
            return checkShorthaulAppointmentNeeded(shipment_type, (_a = sluggify(shipmentStatus)) === null || _a === void 0 ? void 0 : _a.toLowerCase());
        });
    }
    transformFilterModelQuery(useStateFilterModel = false) {
        const modelParams = this.getModelParams(useStateFilterModel);
        return transformFilterModelQuery(modelParams);
    }
    renderPagination() {
        var _a;
        return (React.createElement("span", { "data-testid": "span-5106d5a3", className: "flex justify-between lg:space-x-8 items-center" },
            React.createElement(Pagination, { dataTestId: "Pagination-48096613", first: true, last: true, total: this.state.totalCount, page: this.state.activePage, perPage: this.state.queryPayload.options.per_page, onPageClick: (page) => this.handlePagination(page) }),
            React.createElement(PanelSection, { dataTestId: "PanelSection-63cdade0", className: "flex space-x-2 items-center" },
                React.createElement(PerPage, { options: perPageOptions, onChange: (e) => this.handlePerPageChange(e.target.value), selectedValue: this.state.queryPayload.options.per_page }),
                React.createElement(ColumnCategoriesDropdown, { api: (_a = this.gridRef.current) === null || _a === void 0 ? void 0 : _a.api }))));
    }
    loadBreadCrumbs() {
        const { t, pageContext, groups } = this.props;
        const { updatePage } = pageContext || {};
        if (this.state.report) {
            const { report } = this.state;
            const pages = [];
            let group;
            if (report.groupId)
                group = groups.getGroupById(report.groupId);
            if (pageContext.previousPage) {
                pages.push({
                    name: pageContext.previousPageName,
                    href: pageContext.previousPage,
                });
                if (pageContext.previousPageName === (group === null || group === void 0 ? void 0 : group.name)) {
                    let currentGroup = group;
                    while (currentGroup === null || currentGroup === void 0 ? void 0 : currentGroup.parent) {
                        const parentGroup = groups.getGroupById(currentGroup.parent);
                        pages.unshift({
                            name: parentGroup.name,
                            href: `/unifiedview/groups/${parentGroup._id}/reports`,
                        });
                        currentGroup = parentGroup;
                    }
                }
            }
            else if (group) {
                pages.push({
                    name: group.name,
                    href: `/unifiedview/groups/${report.groupId}/reports`,
                });
                let currentGroup = group;
                while (currentGroup === null || currentGroup === void 0 ? void 0 : currentGroup.parent) {
                    const parentGroup = groups.getGroupById(currentGroup.parent);
                    pages.unshift({
                        name: parentGroup.name,
                        href: `/unifiedview/groups/${parentGroup._id}/reports`,
                    });
                    currentGroup = parentGroup;
                }
            }
            const breadcrumbs = {
                homeUrl: '/unifiedview/reports',
                onClick: updatePage,
                pages: [
                    ...pages,
                    {
                        name: report.name,
                        href: `/unifiedview`,
                        current: true,
                        render: () => {
                            return (React.createElement("div", { "data-testid": "div-ae8c4f22", className: "flex items-center space-x-2" },
                                React.createElement("span", { "data-testid": "span-ede2cd20" }, report.name),
                                React.createElement(DropdownMenu, { dataTestId: "DropdownMenu-c7f46423", useIcon: true },
                                    this.state.report && (React.createElement(Menu.Item, { "data-testid": "MenuItem-3e12d060", key: "save_report" },
                                        React.createElement(DropdownItem, { useIcon: true, dataTestId: "updateReportButton", onClick: this.handleShowSaveReportModal, disabled: !SessionStore.isAbleToManageUVReport(report), disabledText: t('YOU_HAVE_NO_PERMISSIONS_TO_UPDATE_REPORT') }, t('UPDATE_REPORT')))),
                                    React.createElement(Menu.Item, { "data-testid": "MenuItem-9e4e87bb", key: "save_new_report" },
                                        React.createElement(DropdownItem, { useIcon: true, dataTestId: "saveNewReportButton", onClick: this.handleShowSaveNewReportModal }, t('SAVE_NEW_REPORT'))),
                                    this.state.report && (React.createElement(Menu.Item, { "data-testid": "MenuItem-dad3bdaa", key: "delete_rate" },
                                        React.createElement(DropdownItem, { dataTestId: "deleteReportButton", onClick: this.handleShowConfirmDeleteReportModal, disabled: !SessionStore.isAbleToManageUVReport(report), disabledText: t('YOU_HAVE_NO_PERMISSIONS_TO_DELETE_REPORT') }, t('DELETE_REPORT')))),
                                    React.createElement(Menu.Item, { "data-testid": "MenuItem-70fc7fdc", key: "export_report" },
                                        React.createElement(DropdownItem, { useIcon: true, dataTestId: "exportReportButton", onClick: this.exportCsv }, t('EXPORT_TO_CSV'))))));
                        },
                    },
                ],
            };
            this.setState({ breadcrumbs });
            return;
        }
        const breadcrumbs = {
            homeUrl: '/unifiedview/reports',
            onClick: updatePage,
            pages: [
                {
                    name: 'Explore',
                    href: `/unifiedview/explore`,
                    current: true,
                    render: () => {
                        return (React.createElement("div", { "data-testid": "div-50c7a082", className: "flex items-center space-x-2" },
                            React.createElement("span", { "data-testid": "span-7e48723e" }, t('EXPLORE')),
                            React.createElement(DropdownMenu, { dataTestId: "DropdownMenu-b4609936", useIcon: true },
                                React.createElement(Menu.Item, { "data-testid": "MenuItem-34e9993e", key: "save_new_report" },
                                    React.createElement(DropdownItem, { useIcon: true, dataTestId: "saveNewReportButton", onClick: this.handleShowSaveNewReportModal }, t('SAVE_NEW_REPORT'))),
                                React.createElement(Menu.Item, { "data-testid": "MenuItem-639b165e", key: "export_report" },
                                    React.createElement(DropdownItem, { useIcon: true, dataTestId: "exportReportButton", onClick: this.exportCsv }, t('EXPORT_TO_CSV'))))));
                    },
                },
            ],
        };
        this.setState({ breadcrumbs });
    }
    render() {
        var _a, _b, _c, _d, _f, _g, _h;
        const { userAllowed } = this.state;
        if (!userAllowed) {
            return React.createElement(CountDown, null);
        }
        const { t } = this.props;
        const { match } = this.props;
        const params = match === null || match === void 0 ? void 0 : match.params;
        const groupId = (_a = this.state.report) === null || _a === void 0 ? void 0 : _a.groupId;
        const activeTab = groupId ? 'reports' : 'explore';
        const isSavedReport = !!(params === null || params === void 0 ? void 0 : params.reportId);
        return (React.createElement(Layout, { groupId: groupId, activeTab: activeTab, sidebarDefaultState: SIDEBAR_STATUSES.COLLAPSED },
            React.createElement("div", { "data-testid": "div-d75254a9", className: "px-4 sm:px-6 lg:px-8" },
                React.createElement("div", { "data-testid": "div-84559398", className: "" },
                    React.createElement("div", { "data-testid": "div-27313d28", className: "flex space-x-4" },
                        React.createElement(Breadcrumbs, { dataTestId: "Breadcrumbs-ea92556d", breadcrumbs: this.state.breadcrumbs }),
                        this.state.report && (React.createElement(Favorite, { selected: (_b = this.state.report) === null || _b === void 0 ? void 0 : _b.isFavorite, onToggle: this.handleToggleFavorite }))),
                    React.createElement(Panel, { dataTestId: "Panel-1084a244" },
                        React.createElement(PanelSection, { dataTestId: "PanelSection-fe125a89" },
                            React.createElement("div", { "data-testid": "div-43cab497", className: "flex justify-between flex-wrap items-center space-x-4" },
                                React.createElement(FilterChips, { className: "flex-1", columnDefs: this.state.columnDefs, filterModel: (_d = (_c = this.gridRef.current) === null || _c === void 0 ? void 0 : _c.api) === null || _d === void 0 ? void 0 : _d.getFilterModel(), onRemove: this.removeFilter, view: this.state.view }),
                                React.createElement("div", { "data-testid": "div-d4fc9b45", className: "flex space-x-4 items-center ml-auto" },
                                    React.createElement("a", { "data-testid": "a-5708e095", href: "#", className: "w-20 block", onClick: this.reset }, t('CLEAR_FILTERS')),
                                    !isSavedReport && (React.createElement(DropdownMenu, { dataTestId: "selectShipmentType", title: ((_f = getOptions(t).find((option) => option.value === this.state.view)) === null || _f === void 0 ? void 0 : _f.name) || t('SELECT_VIEW') }, getOptions(t).map((option) => (React.createElement(Menu.Item, { key: option.value },
                                        React.createElement(DropdownItem, { dataTestId: `selectShipmentType-SelectItem-${option.value}`, onClick: () => this.handleViewChange(option.value), disabled: option.value === 'shorthaul' &&
                                                this.state.selectedEntityType === 'order' }, option.name)))))),
                                    this.props.enableOrdersUnifiedView &&
                                        this.state.view === 'drayage' && (React.createElement(DropdownMenu, { dataTestId: "DropdownMenu-shipment-order", title: this.state.selectedEntityType === 'shipment'
                                            ? t('SHIPMENT')
                                            : t('ORDER') },
                                        React.createElement(Menu.Item, { "data-testid": "MenuItem-shipment", key: "shipment" },
                                            React.createElement(DropdownItem, { dataTestId: "shipmentOption", onClick: () => this.handleEntityTypeChange('shipment') }, t('SHIPMENT'))),
                                        React.createElement(Menu.Item, { "data-testid": "MenuItem-order", key: "order" },
                                            React.createElement(DropdownItem, { dataTestId: "orderOption", onClick: () => this.handleEntityTypeChange('order') }, t('ORDER'))))),
                                    this.state.selectedEntityType !== 'order' ? (React.createElement(BulkActions, { view: this.state.view, disabled: this.state.selectedShipments.length === 0, changeCarrierDisabled: this.state.changeCarrierDisabled, changeShipperDisabled: this.state.changeShipperDisabled, arrivedActionDisabled: this.state.arrivedActionDisabled, bulkUploadDocumentsDisabled: this.state.bulkUploadDocumentsDisabled, isSameShipperOrMasterShipper: this.state.isSameShipperOrMasterShipper, selectedShipments: this.state.selectedShipments, selectedRows: ((_g = this.gridRef.current) === null || _g === void 0 ? void 0 : _g.getSelectedRows()) || [], selectedShipmentsForYards: this.state.selectedShipmentsForYards, selectedShipmentsForAccessorials: this.state.selectedShipmentsForAccessorials, onBillingStatusPendingReviewUpdate: this.handleUpdateRows })) : (React.createElement(OrdersBulkActions, { view: this.state.view, disabled: this.state.selectedShipments.length === 0, selectedOrders: ((_h = this.gridRef.current) === null || _h === void 0 ? void 0 : _h.getSelectedRows()) || [] })))),
                            this.state.exportLoading && (React.createElement(Alert, { className: "mt-2", type: "warning", title: t('UV_EXPORT_ALERT_TITLE') }, t('UV_EXPORT_ALERT_MSG')))),
                        this.renderPagination(),
                        React.createElement("div", { className: "mt-4 px-8 py-4 ag-theme-balham h-[90vh]" }, this.state.selectedEntityType === 'shipment' ? (React.createElement(ShipmentExplorer, { ref: this.gridRef, items: this.state.items, getRowId: this.getRowId, components: this.state.frameworkComponents, statusBar: this.state.statusBar, columnDefs: this.state.columnDefs, onSortChanged: this.onSortChanged, onSelectionChanged: this.handleRowSelection, onRowDisplayed: this.handleRowDisplayed, stopGroupStats: this.state.stopGroupStats, onFilterChange: this.onFilterChange, onEditingStopped: this.onEditingStopped, shouldFetchData: !isNil(this.state.unifiedViewV2), fetchData: this.fetchData, localeText: this.localeText, loadingText: t('LOADING'), sidebarParams: sidebarParams })) : (React.createElement(OrderExplorer, { ref: this.gridRef, onTotalCountChange: (count) => this.setState({ totalCount: count }), onSelectionChanged: this.handleRowSelection, sidebarParams: sidebarParams }))))),
                this.state.showSaveFilterModal && (React.createElement(SaveFilterModal, { filterSelected: this.state.filterSelected, selectedFilter: this.state.selectedFilter, onUpdate: this.update, setShareWithTeam: this.toggleShare, share: this.state.shared, onSave: this.saveAs, show: true, onHide: this.hideSaveFilterModal, canShareWithTeam: this.state.canShare })),
                this.state.showOrderModal && (React.createElement(CargomaticModal, { dataTestId: "CargomaticModal-226ea836", show: true, location: "top", title: titleize(t('DETAIL', { count: 2 })), onHide: this.onHideModal, body: React.createElement(OrderModule, { id: this.state.order_oid, renderedInModal: true }), footer: null, dialogClassName: "w-11/12" })),
                this.state.showShipmentModal && (React.createElement(CargomaticModal, { dataTestId: "CargomaticModal-82d68cc1", className: "w-full", show: true, onHide: this.onHideShipmentModal, title: titleize(t('DETAIL', { count: 2 })), body: React.createElement(ShipmentModule, { modalID: this.state.shipment_oid }), footer: null, location: "top", dialogClassName: "w-11/12" }))),
            React.createElement(SaveModal, { isOpen: this.state.showSaveModal, onClose: this.handleHideSaveReportModal, isNew: this.state.isSavingNewReport, onSave: this.handleSaveReport, report: this.state.report }),
            React.createElement(DeleteReportModal, { isOpen: this.state.showConfirmDeleteReportModal, onClose: this.handleHideConfirmDeleteReportModal, onConfirm: this.handleConfirmDelete })));
    }
}
export default withTranslation()(withLocaleContext(withPageContext(withGroups(MasterList))));
